/**
 * Line-breaking helpers for angled bar-chart X-axis (location) labels.
 * Pure string utilities — no React or SVG concerns.
 */

/** Insert hyphens and split tokens longer than this many characters. */
export const BAR_TICK_HYPHEN_CHUNK_CHARS = 12;

/** Target max width (character count) for each displayed line after packing. */
export const BAR_TICK_MAX_LINE_CHARS = 17;

const packWordsIntoLines = (words: string[], maxLineLength: number): string[] => {
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if (!word) {
      continue;
    }
    const candidate = currentLine ? `${currentLine} ${word}` : word;

    if (candidate.length <= maxLineLength) {
      currentLine = candidate;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
};

/**
 * Splits a single word into segments of at most {@code maxChunkChars},
 * appending "-" to every segment except the last (SVG tick line breaks).
 */
const hyphenateLongWord = (word: string, maxChunkChars: number): string[] => {
  if (word.length <= maxChunkChars) {
    return [word];
  }

  const segments: string[] = [];
  let i = 0;

  while (i < word.length) {
    const remaining = word.length - i;

    if (remaining <= maxChunkChars) {
      segments.push(word.slice(i));
      break;
    }

    segments.push(`${word.slice(i, i + maxChunkChars)}-`);
    i += maxChunkChars;
  }

  return segments;
};

/**
 * Wraps whitespace-delimited words into lines of at most {@code maxLineLength} characters.
 * Preserves legacy behavior for callers that only need space-based wrapping.
 */
export const splitTextBySpace = (text: string, maxLineLength: number): string[] =>
  packWordsIntoLines(text.split(/\s+/).filter(Boolean), maxLineLength);

/**
 * Hyphenates long tokens, then packs into lines for angled location labels on the emissions-by-location bar chart.
 */
export const buildBarChartLocationTickLines = (text: string): string[] => {
  const words = text.split(/\s+/).filter(Boolean);
  const tokens = words.flatMap(w => hyphenateLongWord(w, BAR_TICK_HYPHEN_CHUNK_CHARS));
  return packWordsIntoLines(tokens, BAR_TICK_MAX_LINE_CHARS);
};
